import { supabase } from '../lib/supabase'
import { LINE_COLUMNS, toLine, type OrderLine } from './orders'

// Stock and ESL list price moved to ./stock.ts (public.rep_stock_price,
// migration 0033). public.stock, the old seed table, is no longer read.

/**
 * The caller's own lines for one item, from public.order_lines_display
 * (migration 0029, security_invoker): order_lines_rep_read restricts every
 * row to orders the caller is credited on via order_reps, so a plain
 * `eq('item_id', ...)` is already "my own lines". Nothing is filtered
 * client-side. Same line rendering as order detail (components/LineCard).
 */
export type SkuOrderLinesResult =
  | { status: 'ok'; lines: OrderLine[]; totalCount: number }
  | { status: 'error'; detail: string }

/**
 * `limit` caps how many rows come back — a rep can have hundreds of lines
 * against one item, and this is a phone screen. `totalCount` (PostgREST's
 * exact count, same request) is what a "show more" control needs to know
 * whether there's anything left to reveal.
 *
 * Newest order first. order_no is P21's sequential order number, so it
 * orders lines by when they were entered; the old sort key (the 0008
 * resolved required/expedite date) is retired with that rule.
 */
export async function fetchSkuOrderLines(
  itemId: string,
  limit: number,
): Promise<SkuOrderLinesResult> {
  const { data, error, count } = await supabase
    .from('order_lines_display')
    .select(LINE_COLUMNS, { count: 'exact' })
    .eq('item_id', itemId)
    .order('order_no', { ascending: false })
    .order('line_no', { ascending: true })
    .range(0, limit - 1)

  if (error) return { status: 'error', detail: error.message }

  const rows = data as unknown as Parameters<typeof toLine>[0][]
  return { status: 'ok', lines: rows.map(toLine), totalCount: count ?? rows.length }
}
